import React from 'react';
import { toast as hot } from 'react-hot-toast';
import type { ToastOptions } from 'react-hot-toast';
import { Toast, ToastVariant } from './components/Toast';

/**
 * On-brand toast API. Drop-in replacement for `react-hot-toast`'s default export:
 * keep your call sites as `toast.success(...)` / `toast.error(...)` and only swap
 * the import to this module. Renders the design-system <Toast> via toast.custom.
 */

/** Default on-screen durations (ms). Kept here as the single place to tune them. */
const DURATION: Record<ToastVariant, number> = {
  success: 2500,
  info: 3000,
  warning: 3500,
  error: 4000,
  loading: Infinity,
};

export interface NotifyOptions extends ToastOptions {
  /** Optional bold lead line rendered above the message. */
  title?: React.ReactNode;
}

function show(variant: ToastVariant, message: React.ReactNode, opts: NotifyOptions = {}) {
  const { title, duration, ...rest } = opts;
  return hot.custom(
    (t) => (
      <Toast
        variant={variant}
        message={message}
        title={title}
        visible={t.visible}
        onDismiss={() => hot.dismiss(t.id)}
      />
    ),
    { duration: duration ?? DURATION[variant], ...rest },
  );
}

const notify = Object.assign(
  // Callable form: `toast('message')` → info variant (matches react-hot-toast).
  (message: React.ReactNode, opts?: NotifyOptions) => show('info', message, opts),
  {
    success: (message: React.ReactNode, opts?: NotifyOptions) => show('success', message, opts),
    error: (message: React.ReactNode, opts?: NotifyOptions) => show('error', message, opts),
    warning: (message: React.ReactNode, opts?: NotifyOptions) => show('warning', message, opts),
    info: (message: React.ReactNode, opts?: NotifyOptions) => show('info', message, opts),
    loading: (message: React.ReactNode, opts?: NotifyOptions) =>
      show('loading', message, { duration: Infinity, ...opts }),
    // Pass-throughs for completeness / safety.
    custom: hot.custom,
    dismiss: hot.dismiss,
    remove: hot.remove,
    promise: hot.promise,
  },
);

export { notify };
export default notify;
